import {
  addDays,
  addMonths,
  endOfDay,
  format,
  getDate,
  getISODay,
  lastDayOfISOWeek,
  lastDayOfMonth,
  startOfDay,
  subDays,
  addWeeks,
  startOfYesterday,
  isYesterday,
  isThisWeek,
  eachDayOfInterval,
  startOfMonth,
  endOfMonth,
  eachHourOfInterval,
  addYears,
  getDayOfYear,
  lastDayOfYear,
  isThisYear,
  addMinutes,
} from 'date-fns';

export type MeasurementType = {
  startAt: string;
  source?: string;
  value: number;
  unit: string;
  readAt: string;
};

export enum DateRange {
  DAY = 'DAY',
  WEEK = 'WEEK',
  MONTH = 'MONTH',
  YEAR = 'YEAR',
}

export const capitalizeFirstCharOnly = (string: string) => {
  return string[0] + string.slice(1).toLocaleLowerCase();
};

export const YESTERDAY = startOfYesterday();

// For a given anchor date, create a mapping of startAt and endAt dates for each date range
export const createDateRangeStartAtEndAtMapping = (anchorDate: Date) => {
  return {
    [DateRange.DAY]: {
      startAt: startOfDay(anchorDate),
      endAt: endOfDay(anchorDate),
    },
    [DateRange.WEEK]: {
      startAt: startOfDay(subDays(anchorDate, getISODay(anchorDate) - 1)),
      endAt: lastDayOfISOWeek(anchorDate),
    },
    [DateRange.MONTH]: {
      startAt: startOfDay(subDays(anchorDate, getDate(anchorDate) - 1)),
      endAt: lastDayOfMonth(anchorDate),
    },
    [DateRange.YEAR]: {
      startAt: startOfDay(subDays(anchorDate, getDayOfYear(anchorDate) - 1)),
      endAt: isThisYear(anchorDate)
        ? endOfMonth(new Date())
        : lastDayOfYear(anchorDate),
    },
  };
};

export const DATE_RANGE_SET_ANCHOR_DATE_MAPPING = {
  [DateRange.DAY]: addDays,
  [DateRange.WEEK]: addWeeks,
  [DateRange.MONTH]: addMonths,
  [DateRange.YEAR]: addYears,
};

export const getDateRangeDisplay = (
  range: DateRange,
  startAt: Date,
  endAt: Date
): string => {
  switch (range) {
    case DateRange.DAY:
      return isYesterday(startAt) ? 'Yesterday' : format(startAt, 'PPP');

    case DateRange.WEEK:
      return isThisWeek(startAt)
        ? 'This week'
        : isThisWeek(addWeeks(startAt, 1))
          ? 'Last week'
          : `${format(startAt, 'MMM d')} - ${format(endAt, 'MMM d, yyyy')}`;

    case DateRange.MONTH:
      return format(startAt, 'MMMM yyyy');

    case DateRange.YEAR:
      return format(startAt, 'YYY');

    default:
      return '';
  }
};

export const transformBackendDataToChartFriendly = (
  data: { node: MeasurementType }[] | undefined,
  dateRangeToggleValue: string,
  dateRangeStartAtEndAtMapping: { [x: string]: { startAt: Date; endAt: Date } }
): { x: string; y: number }[] => {
  const { startAt, endAt } = dateRangeStartAtEndAtMapping[dateRangeToggleValue];
  const startDate = new Date(startAt);
  const endDate = new Date(endAt);

  const grouped: Record<string, number> = {};

  // Helper function to group the data based on date range
  const groupDataByRange = (usageDate: Date, value: number) => {
    let key = '';

    switch (dateRangeToggleValue) {
      case 'DAY':
        key = format(usageDate, 'HH');
        break;
      case 'WEEK':
        key = format(usageDate, 'EE').toUpperCase();
        break;
      case 'MONTH':
        key = format(usageDate, 'd');
        break;
      case 'YEAR':
        key = format(usageDate, 'MMM').toUpperCase();
        break;
      default:
        return null;
    }

    grouped[key] = (grouped[key] || 0) + Number(value);
  };

  // Iterate over the data and process based on the range
  data?.forEach(({ node }) => {
    const usageDate = new Date(node.startAt); // Use startAt for grouping

    if (
      usageDate.getTime() >= startDate.getTime() &&
      usageDate.getTime() <= endDate.getTime()
    ) {
      // Convert to litres
      let convertedValue =
        node.unit.toLowerCase() === 'm3' ? node.value * 1000 : node.value;

      // Convert string to number if necessary
      convertedValue = Number(convertedValue);

      // Filter out negative or invalid values
      if (isNaN(convertedValue) || convertedValue < 0) {
        return;
      }

      groupDataByRange(usageDate, convertedValue);
    }
  });

  // Prepare the result based on the selected date range
  const results: { x: string; y: number }[] = [];

  const generateResultsForDay = () => {
    eachHourOfInterval({ start: startDate, end: endDate }).forEach((hour) => {
      const key = format(hour, 'HH');

      results.push({ x: key, y: grouped[key] || 0 });
    });
  };

  const generateResultsForWeek = () => {
    eachDayOfInterval({ start: startDate, end: endDate }).forEach((day) => {
      const key = format(day, 'EE').toUpperCase();

      results.push({ x: key, y: grouped[key] || 0 });
    });
  };

  const generateResultsForMonth = () => {
    eachDayOfInterval({
      start: startOfMonth(startDate),
      end: endOfMonth(startDate),
    }).forEach((day) => {
      const key = format(day, 'd');

      results.push({ x: key, y: grouped[key] || 0 });
    });
  };

  const generateResultsForYear = () => {
    Array.from({ length: 12 }).forEach((_, i) => {
      const month = addMonths(startDate, i - startDate.getMonth());
      const key = format(month, 'MMM').toUpperCase();

      results.push({ x: key, y: grouped[key] || 0 });
    });
  };

  // Choose the appropriate generation logic based on the selected range
  switch (dateRangeToggleValue) {
    case 'DAY':
      generateResultsForDay();
      break;
    case 'WEEK':
      generateResultsForWeek();
      break;
    case 'MONTH':
      generateResultsForMonth();
      break;
    case 'YEAR':
      generateResultsForYear();
      break;
    default:
      break;
  }

  return results;
};

// The backend requires a bump forwards in time slightly for the end dates to capture necessary data
export const formatEndDatesForSmartDataThresholds = (endAt: Date) => {
  return {
    [DateRange.DAY]: addMinutes(endAt, 10),
    [DateRange.WEEK]: addDays(endAt, 2),
    [DateRange.MONTH]: addDays(endAt, 2),
    [DateRange.YEAR]: endAt,
  };
};
